import { Injectable } from '@angular/core';
import { filter, take } from 'rxjs/operators';

import { FileItem, FileType } from '../models/file-item.model';
import { ObservableScope } from '../models/observable-scope.enum';
import { ConfigParserService } from '../services/config-parser.service';
import { DocumentService } from '../services/document.service';
import { EventService } from '../services/event.service';
import { AngularEvents } from '../shared-context/angular-events';
import { ContextService } from '../shared-context/context.service';
import { DOMInteractiveMarker } from '../utilities/dom-interactive-marker.utility';
import { UrlBuilder } from '../utilities/url-builder.utility';
import { RawTemplatesResponse } from './templates.model';

export interface TemplatesConfig {
    createLcidEnabled: boolean;
    enterpriseTemplatesEnabled: boolean;
    exploreTemplatesUrlFormat: string;
    exploreTemplatesUrlLocale: string;
    msCreateSupportedLocale: string;
    numTemplates: number;
    numVisioTemplates: number;
    premiumTemplatesDiamondCorner: string;
    premiumTemplatesUpsellEnabled: boolean;
    sharePointAuthFrameUrl: string;
    templatesApiEndpoint: string;
    excelOrgTemplatesEnabled: boolean;
    templatesFormsEnabled: boolean;
    createTemplatesEnabled: boolean;
    wordOrgTemplatesEnabled: boolean;
    templatesRecommendedApps: Array<string>;
    templatesCollectionsCommercial: Array<string>;
    templatesCollectionsConsumer: Array<string>;
    templatesCollectionsEdu: Array<string>;
    templatesRecommendedApiEnabled: boolean;
    visioUnsupportedTemplatesLocales: Array<string>;
}

export interface TemplatesResources {
    BreadCrumbsAriaLabel: string;
    CreateFileDialogOneNoteInputPlaceholder: string;
    CreateFromDocumentAriaLabel: string;
    CreateFromDocumentText: string;
    CreateFromTemplateAriaLabel: string;
    CreateNewBlankAriaLabel: string;
    CreateNewBlankText: string;
    CreateNewBlankAriaLabelStream: string;
    CreateNewBlankTextStream: string;
    CreateNewBlankAriaLabelPlayList: string;
    CreateNewBlankTextPlayList: string;
    CreateNewBlankAriaLabelScreenRecording: string;
    CreateNewBlankTextScreenRecording: string;
    CreateNewBlankAriaLabelForms: string;
    CreateNewBlankTextForms: string;
    CreateNewBlankAriaLabelFormsQuiz: string;
    CreateNewBlankTextFormsQuiz: string;
    CreateNewGroupFormAriaLabel: string;
    CreateNewGroupFormText: string;
    CreateNewGroupQuizAriaLabel: string;
    CreateNewGroupQuizText: string;
    ExploreTemplatesText: string;
    FluidUnsupportedBrowser: string;
    LicenseStatusApiPath: string;
    MicrosoftEdgeIcon: string;
    GoogleChromeIcon: string;
    MozillaFirefoxIcon: string;
    SafariIcon: string;
    NoTemplatesMessage: string;
    PrefetchUrl: string;
    PremiumTemplatesHookTitle: string;
    PremiumTemplatesUpsellThumbnail: string;
    StandardTemplatesHeaderTitle: string;
    StartPageCreateFromTemplateAriaLabel: string;
    StartPageCreateFromTemplateText: string;
    StartPageHideTemplatesAriaLabel: string;
    StartPageHideTemplatesText: string;
    StartPageMoreTemplatesAriaLabel: string;
    StartPageMoreTemplatesText: string;
    StartPageShowTemplatesAriaLabel: string;
    StartPageShowTemplatesText: string;
    TemplateContainerAriaLabel: string;
    TemplatesErrorMessage: string;
    TemplatesPageTitle: string;
}

interface InternalTemplatesConfig extends TemplatesConfig {
    appResources: AppResources;
}

interface AppResources {
    common: Partial<TemplatesResources>;
    variants?: Array<AppVariant>;
}

interface AppVariant {
    app: string;
    resources: Partial<TemplatesResources>;
}

@Injectable({
    providedIn: 'root'
})
export class TemplatesConfigService {
    public config: TemplatesConfig;

    private static readonly configElementId = 'templatesPageConfig';
    private readonly resourceMap: Map<FileType, TemplatesResources>;
    private readonly resourceVariants: Map<FileType, Partial<TemplatesResources>>;
    private readonly commonResources: Partial<TemplatesResources>;
    private templatesInfoPromise: Promise<RawTemplatesResponse>;

    constructor(
        private readonly configParserService: ConfigParserService,
        private readonly contextService: ContextService,
        private readonly documentService: DocumentService,
        private readonly eventService: EventService
    ) {
        const parsedConfig = this.configParserService.parse<InternalTemplatesConfig>(TemplatesConfigService.configElementId);
        this.config = parsedConfig || {
            createLcidEnabled: false,
            enterpriseTemplatesEnabled: false,
            exploreTemplatesUrlFormat: null,
            exploreTemplatesUrlLocale: null,
            msCreateSupportedLocale: '',
            numTemplates: 0,
            numVisioTemplates: 0,
            premiumTemplatesDiamondCorner: null,
            premiumTemplatesUpsellEnabled: false,
            sharePointAuthFrameUrl: null,
            templatesApiEndpoint: null,
            excelOrgTemplatesEnabled: false,
            templatesFormsEnabled: false,
            createTemplatesEnabled: false,
            wordOrgTemplatesEnabled: false,
            templatesRecommendedApps: [],
            templatesCollectionsCommercial: [],
            templatesCollectionsConsumer: [],
            templatesCollectionsEdu: [],
            templatesRecommendedApiEnabled: false,
            visioUnsupportedTemplatesLocales: []
        };
        this.commonResources = parsedConfig && parsedConfig.appResources ? parsedConfig.appResources.common : {};
        this.resourceVariants = new Map<FileType, Partial<TemplatesResources>>();
        if (parsedConfig && parsedConfig.appResources && parsedConfig.appResources.variants) {
            parsedConfig.appResources.variants.forEach(entry => {
                const type = FileItem.fileTypeNameToFileType(entry.app);
                if (type) {
                    this.resourceVariants.set(type, entry.resources);
                }
            });
        }
        this.resourceMap = new Map<FileType, TemplatesResources>();
    }

    public resources(fileType: FileType = FileType.Word): TemplatesResources {
        const app = FileItem.fileTypeToApp(fileType);
        if (this.resourceMap.has(app)) {
            return this.resourceMap.get(app);
        }

        if (!this.resourceVariants.has(app)) {
            return this.commonResources as TemplatesResources;
        }

        const variants = this.resourceVariants.get(app);
        const fileResources = { ...this.commonResources, ...variants } as TemplatesResources;
        this.resourceMap.set(app, fileResources);
        return fileResources;
    }

    public getTemplatesInfoPromise(): Promise<RawTemplatesResponse> {
        if (!this.templatesInfoPromise) {
            const tokensLoadedPromise = this.eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
                filter(event => event.name === AngularEvents.primaryTokensLoaded),
                take(1))
                .toPromise();
            const domReadyPromise = DOMInteractiveMarker.fullPageReady();
            this.templatesInfoPromise = Promise.race([tokensLoadedPromise, domReadyPromise]).then(() => this.configParserService.parse<RawTemplatesResponse>('templatesInfo'));
        }

        return this.templatesInfoPromise;
    }

    public prefetchStaticResources(fileType: FileType) {
        const templatesResources = this.resources(fileType);
        if (templatesResources?.PrefetchUrl) {
            this.documentService.addIframeToPage(UrlBuilder.appendQueryParam(templatesResources.PrefetchUrl, 'ui', this.contextService.sharedContext.officeMarket));
        }
    }
}
